/**
 * Study Timeline page – UI strings and messages.
 * Centralized for consistency and easier i18n later.
 */

// ─── Controls & labels (displayed blue, bold, uppercase to the left of controls) ──
export const LABELS = {
  SELECT_ARM: 'SELECT ARM',
  NO_ARMS_AVAILABLE: 'No arms available',
  INCLUDE_VISIT_CYCLE: 'INCLUDE VISIT CYCLE',
  SELECT_START_DATE_ABSOLUTE: 'SELECT START DATE FOR ABSOLUTE',
  SELECT_DAY: 'SELECT DAY',
  DISPLAY_TYPE: 'DISPLAY TYPE',
  FILTER_BY_INSTRUMENT: 'FILTER BY INSTRUMENT',
  ALL_INSTRUMENTS: 'All instruments',
  GO: 'GO',
  OVERVIEW: 'Overview',
  SCHEDULED: 'Scheduled',
  EXPAND: 'Expand',
  COLLAPSE: 'Collapse',
  EXPAND_ALL: 'Expand All',
  COLLAPSE_ALL: 'Collapse All',
  SELECT_START_DATE: 'Select start date',
}

// ─── Messages (longer text) ───────────────────────────────────────────────
export const MESSAGES = {
  ABSOLUTE_DATE_HELP:
    '^ This checkbox must be enabled for all instruments deployed on the specified exact date.',
  NO_DAYS_DATA: 'No days data available. Please check your study configuration.',
  CONFIGURE_AND_GO: 'Please configure your options above and click "Go" to load the timeline.',
  NO_EVENTS_THIS_DAY: 'No events scheduled for this day.',
  NO_EVENTS: 'No events',
}

// ─── View types (option values) ────────────────────────────────────────────
export const VIEW_TYPES = {
  OVERVIEW: 'overview',
  SCHEDULED: 'scheduled',
}

// ─── Time & schedule display ───────────────────────────────────────────────
export const TIME = {
  DEFAULT_VISIT_TIME: '9 - 10am',
  DEFAULT_INSTRUMENT_TIME: '9am (+Never Expire)',
  ALL_DAY: 'All Day',
  IMMEDIATELY: 'Immediately',
  NEVER: 'Never',
  NEVER_EXPIRE: 'Never Expire',
  WHEN_COMPLETE: 'When Complete',
  NO_REPEAT: 'No Repeat',
  ALL_DAY_VISIBILITY: 'All day',
  AT_SPECIFIC_TIMES: 'At specific times',
  EXACT_DATE: 'Exact Date',
  IMMEDIATELY_AFTER_TRIGGER: 'Immediately after trigger',
  CURRENT_DATE: 'Current date',
}

// ─── Legend ────────────────────────────────────────────────────────────────
export const LEGEND = {
  CYCLE_ACTIVE: 'Cycle scheduled',
  VISIT_FOR_DAY: 'Visit scheduled',
  INSTRUMENT_DEPLOYMENT_DAY: 'Instrument scheduled',
}

// Light colour palette for instrument type in day detail view (border + heading). Legend swatch uses same as border.
export const INSTRUMENT_TYPE_COLORS = {
  SURVEY: { border: '#a8d4e8', headingBg: '#e8f4fc' },
  TASK: { border: '#e8c4b0', headingBg: '#fdf3ef' },
  ARTICLE: { border: '#a8c99e', headingBg: '#eef6ea' },
  CLINRO: { border: '#7eb8a8', headingBg: '#e8f4f1' },
  ANNOUNCEMENT: { border: '#b8a8d4', headingBg: '#f0ecf8' },
  EDIARY: { border: '#d4c878', headingBg: '#faf6e8' },
  CONSENT: { border: '#d4a8b8', headingBg: '#f8eef2' },
}

// Display order and label for instrument type legend (below main legend)
export const INSTRUMENT_TYPE_LEGEND_ORDER = [
  { key: 'ANNOUNCEMENT', label: 'Announcement' },
  { key: 'CLINRO', label: 'Clinician Instrument' },
  { key: 'TASK', label: 'Task' },
  { key: 'SURVEY', label: 'Survey' },
  { key: 'ARTICLE', label: 'Article' },
  { key: 'EDIARY', label: 'Diary' },
  { key: 'CONSENT', label: 'Consent' },
]

// Instrument types shown in the sidebar "Instrument types" legend only (excludes Consent and Announcement –
// they are not considered instruments from the Project Team's perspective, though still rendered on the timeline).
export const INSTRUMENT_TYPES_FOR_LEGEND = INSTRUMENT_TYPE_LEGEND_ORDER.filter(
  ({ key }) => key !== 'CONSENT' && key !== 'ANNOUNCEMENT'
)

// Consent and Announcement shown in a separate legend section (above or below Instrument types).
export const CONSENT_ANNOUNCEMENT_LEGEND = [
  { key: 'ANNOUNCEMENT', label: 'Announcement' },
  { key: 'CONSENT', label: 'Consent' },
]

// ─── Fallbacks & empty values ──────────────────────────────────────────────
export const FALLBACKS = {
  EMPTY_DASH: '—',
  CYCLE: 'Cycle',
  VISIT: 'Visit',
  INSTRUMENT: 'Instrument',
  N_A: 'N/A',
}

// ─── Cycle labels (prefixes for schedule view) ──────────────────────────────
export const CYCLE_LABELS = {
  START_PREFIX: 'Start: ',
  END_PREFIX: 'End: ',
}

// ─── Day display ────────────────────────────────────────────────────────────
export const DAY = {
  LABEL_PREFIX: 'Day ',
  SCHEDULED_PREFIX: 'DAY ',
}

// ─── Instrument detail – cohort types ─────────────────────────────────────────
export const COHORT_TYPE_LABELS = {
  all_participants: 'All Participant',
  all_caregivers: 'All Caregivers',
  all_users: 'Participants & Caregivers',
  sites: 'Sites',
  age: 'Age Range',
  study_group: 'Tracks',
}

// ─── Instrument detail – section & row labels ──────────────────────────────
export const INSTRUMENT_DETAIL = {
  SCHEDULE_ON_DAY_LEGEND: 'Scheduled on selected day',
  SECTION_SCHEDULE: 'Schedule',
  SECTION_SCHEDULES: 'Schedules',
  ALL_SCHEDULES: 'All Schedules',
  EDIT_SCHEDULE: 'Edit Schedule',
  STUDY_DAY: 'Study Day',
  TYPE: 'Type',
  SENT_TO: 'Sent to',
  PLACEMENT: 'Placement',
  RESUME_EDIT: 'Resume/Edit',
  SCORING: 'Scoring',
  TRIGGERS: 'Triggers',
  PRIORITY: 'Priority',
  GAM_PTS: 'Gam Pts',
  GAME_PTS: 'Game Pts',
  START: 'Start',
  EXPIRE: 'Expire',
  REPEAT: 'Repeat',
  VISIBLE: 'Visible',
  YES: 'Yes',
  NO: 'No',
}

// Empty value for instrument detail fields when no value (per ticket: display "-")
export const INSTRUMENT_EMPTY_VALUE = '-'

// Display labels for instrument type (Survey, Task, Article, Clinician instrument, etc.)
export const INSTRUMENT_TYPE_DISPLAY = {
  SURVEY: 'Survey',
  TASK: 'Task',
  ARTICLE: 'Article',
  CLINRO: 'Clinician Instrument',
  EDIARY: 'Diary',
  CONSENT: 'Consent',
  ANNOUNCEMENT: 'Announcement',
}

// ─── Visit detail row labels ────────────────────────────────────────────────
export const VISIT_DETAIL = {
  NAME: 'Name',
  CYCLE: 'Cycle',
  ANCHOR: 'Anchor',
  OFFSET: 'Offset',
  WINDOW: 'Window',
  STUDY_DAY: 'Study Day',
}

// ─── Cycle detail row labels ────────────────────────────────────────────────
export const CYCLE_DETAIL = {
  CYCLE_NAME: 'Cycle Name',
  NAME: 'Name',
  DURATION: 'Duration',
  ANCHOR: 'Anchor',
  OFFSET: 'Offset',
  STUDY_DAY: 'Study Day',
  DAYS_SUFFIX: ' days',
}

// ─── Schedule / deployment strings (used in InstrumentDetailContent) ────────
export const SCHEDULE = {
  ON_DAY_OF_VISIT: 'On day of visit',
  AFTER_VISIT: 'after visit',
  BEFORE_VISIT: 'before visit',
  AFTER_TRIGGER: 'after trigger',
  DAYS_AFTER_FIRST_LOGIN: 'after First Login',
  AFTER_OCCURRENCES: 'After',
  OCCURRENCES: 'occurrences',
  EVERY: 'Every',
  VISIT: 'Visit',
  INSTRUMENT: 'Instrument',
  INSTRUMENTS: 'Instruments',
}

// ─── Module fallbacks (StudyTimeline.js) ────────────────────────────────────
export const MODULE_FALLBACKS = {
  INSTRUMENT_LABEL: 'Instrument',
  VISIT_LABEL: 'Visit',
  CYCLE_NAME_NA: 'N/A',
  INSTRUMENT_NAME_PREFIX: 'Instrument ',
}
